import { ExtendedTractor } from '@hooks/useDriverTractors.types';
import { GridColDef } from '@mui/x-data-grid-pro';
import { isTractorSelectable } from './types';
import { Tooltip } from '@mui/material';
import ErrorOutlineTwoTone from '@mui/icons-material/ErrorOutlineTwoTone';
import MobileTractorItem from './MobileTractorItem';
import { caseInsensitiveIncludes } from '../Dispatch/columns';
import { DateAfterTodayOperator } from '../Filters';

import styles from './DataGrid.Tractor.module.css';

export function getTractorColumnDefinitions(requiresHazmatCert: boolean): GridColDef<ExtendedTractor>[] {
  return [
    {
      field: 'id',
      minWidth: 95,
      width: 95,
      headerName: 'Truck #',
      sortable: false,
      disableReorder: true,
      renderCell: ({ row }) => {
        const selectionStatus = isTractorSelectable(row, requiresHazmatCert);

        return (
          <div
            style={{
              width: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between'
            }}>
            <span>{row.id}</span>
            {!selectionStatus.isSelectable && (
              <Tooltip
                title={
                  <ul className={styles['disabled-reasons-ul']}>
                    {selectionStatus.disabledReason.map((reason, index) => (
                      <li key={`disabled-reason-${String(index)}`}>{reason}</li>
                    ))}
                  </ul>
                }>
                <ErrorOutlineTwoTone className={styles['disabled-warning-icon']} />
              </Tooltip>
            )}
          </div>
        );
      }
    },
    {
      field: 'drvId',
      minWidth: 220,
      width: 220,
      headerName: 'Current Driver',
      flex: 1,
      renderCell: ({ row }) => {
        if (row.drivers?.length === 1) {
          const defaultDriver = row.drivers.find((d) => d.id === row.drvId);
          return defaultDriver ? <span>{`${defaultDriver.name} (${defaultDriver.id})`}</span> : <span>*</span>;
        }
        return row.drivers && row.drivers.length > 1 ? (
          <span
            title='Multiple Drivers Available'
            style={{ width: '100%' }}>
            *
          </span>
        ) : (
          <></>
        );
      },
      getApplyQuickFilterFn: (value: string) => {
        return (_, row: ExtendedTractor) => {
          const defaultDriver = row.drivers?.find((d) => d.id === row.drvId);
          if (!defaultDriver) {
            return false;
          }
          return caseInsensitiveIncludes(value, [defaultDriver.name, defaultDriver.id]);
        };
      }
    },
    {
      field: 'pltNo',
      headerName: 'Plate #',
      minWidth: 95,
      width: 95,
      sortable: false,
      disableReorder: true
    },
    {
      field: 'plateExpiration',
      minWidth: 95,
      width: 95,
      headerName: 'Plate Exp.',
      renderCell: ({ row }) => <span title={row.plateExpirationDate.date}>{row.plateExpirationDate.shortDateExtended}</span>,
      align: 'center',
      sortable: false,
      disableReorder: true
    },
    {
      field: 'inspectionDue',
      minWidth: 125,
      width: 125,
      headerName: 'Last Inspection',
      renderCell: ({ row }) => <span title={row.inspectionDate?.date || 'N/A'}>{row.inspectionDate?.shortDateExtended || 'N/A'}</span>,
      align: 'center',
      sortable: false,
      disableReorder: true
    },
    {
      field: 'hazmatExpirationDate',
      valueGetter: (_, row: ExtendedTractor) => {
        const defaultDriver = row.drivers?.find((d) => d.id === row.drvId);
        if (!defaultDriver) {
          return undefined;
        }
        return defaultDriver.hazmatExpirationDate?.date;
      },
      filterOperators: [DateAfterTodayOperator]
    },
    {
      field: 'isAssignedJob',
      filterable: true,
      valueGetter: (_, row: ExtendedTractor) => {
        const defaultDriver = row.drivers?.find((d) => d.id === row.drvId);
        if (!defaultDriver) {
          return undefined;
        }
        return String(Boolean(defaultDriver.isAssignedJob));
      }
    },
    {
      field: 'isOnLastJob',
      filterable: true,
      valueGetter: (_, row: ExtendedTractor) => {
        const defaultDriver = row.drivers?.find((d) => d.id === row.drvId);
        if (!defaultDriver) {
          return undefined;
        }
        return defaultDriver.isOnLastJob;
      }
    }
  ];
}

export function getTractorMobileColumnDefinitions(requiresHazmatCert: boolean): GridColDef<ExtendedTractor>[] {
  return [
    {
      field: 'Mobile Card',
      flex: 1,
      renderCell: ({ row }) => (
        <MobileTractorItem
          key={row.id}
          tractor={row}
          requiresHazmatCert={requiresHazmatCert}
        />
      ),
      getApplyQuickFilterFn: (value: string) => {
        return (_, row): boolean => {
          const { id, name } = row;
          const searchableValues = [id, name];
          const defaultDriver = row.drivers?.find((d) => d.id === row.drvId);
          if (defaultDriver && row.drivers?.length === 1) {
            searchableValues.push(defaultDriver.name);
          }
          return caseInsensitiveIncludes(value, searchableValues);
        };
      }
    },
    {
      field: 'hazmatExpiration',
      valueGetter: (_, row: ExtendedTractor) => {
        const defaultDriver = row.drivers?.find((d) => d.id === row.drvId);
        if (!defaultDriver) {
          return undefined;
        }
        return defaultDriver.hazmatExpirationDate?.date;
      },
      filterOperators: [DateAfterTodayOperator]
    },
    {
      field: 'isAssignedJob',
      filterable: true,
      valueGetter: (_, row: ExtendedTractor) => {
        const defaultDriver = row.drivers?.find((d) => d.id === row.drvId);
        if (!defaultDriver) {
          return undefined;
        }
        return String(Boolean(defaultDriver.isAssignedJob));
      }
    },
    {
      field: 'isOnLastJob',
      filterable: true,
      valueGetter: (_, row: ExtendedTractor) => {
        const defaultDriver = row.drivers?.find((d) => d.id === row.drvId);
        if (!defaultDriver) {
          return undefined;
        }
        return defaultDriver.isOnLastJob;
      }
    }
  ];
}
